import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, ChevronRight, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useExam } from '@/lib/api-adapters';
import { usePracticeSession } from '@/hooks/usePracticeSession';
import { getContentLocale } from '@/lib/contentLocale';
import { ProgressBar } from '@/components/practice/ProgressBar';
import { QuestionCard } from '@/components/practice/QuestionCard';
import { OptionsList } from '@/components/practice/OptionsList';
import { FeedbackPanel } from '@/components/practice/FeedbackPanel';
import { ResultsSummary } from '@/components/practice/ResultsSummary';
import { ResumePracticeModal, QuitPracticeModal } from '@/components/practice/PracticeSessionModals';
import { registerPracticeNavigationGuard } from '@/lib/practiceNavigationGuard';
import { trackQuizAbandon } from '@/lib/track';
import { useDocumentTitle } from '@/lib/useDocumentTitle';

export function PracticePage() {
  const navigate = useNavigate();
  const { examId, categoryId } = useParams<{ examId: string; categoryId: string }>();
  const [searchParams] = useSearchParams();
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const feedbackRef = useRef<HTMLDivElement>(null);
  const locale = getContentLocale();

  const { exam, loading: examLoading } = useExam(examId);
  useDocumentTitle(exam ? `${exam.name} — Pratique` : 'Pratique');
  const difficulty = searchParams.get('difficulty') as 'easy' | 'medium' | 'hard' | null;
  const countParam = searchParams.get('count');
  const questionCount = countParam ? parseInt(countParam, 10) : undefined;

  const session = usePracticeSession({
    examId: examId || '',
    categoryId: categoryId || '',
    shuffle: true,
    difficulty: difficulty || undefined,
    count: questionCount,
  });

  const openQuitModal = useCallback((destination: string) => {
    setPendingNavigation(destination);
    setShowQuitModal(true);
    return false;
  }, []);

  useEffect(() => {
    if (!session.isSessionInProgress) {
      registerPracticeNavigationGuard(null);
      return;
    }

    registerPracticeNavigationGuard((destination) => {
      if (destination === window.location.pathname) return true;
      return openQuitModal(destination) as unknown as boolean;
    });

    return () => registerPracticeNavigationGuard(null);
  }, [session.isSessionInProgress, openQuitModal]);

  // Intercept browser back button
  useEffect(() => {
    if (!session.isSessionInProgress || !examId) return;

    window.history.pushState({ practiceGuard: true }, '');
    const handlePopState = () => {
      window.history.pushState({ practiceGuard: true }, '');
      openQuitModal(`/exam/${examId}`);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [session.isSessionInProgress, examId, openQuitModal]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (session.isSessionInProgress) {
        session.saveForLater();
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [session.isSessionInProgress, session.saveForLater]);

  const navigateAway = useCallback((path: string) => {
    setShowQuitModal(false);
    setPendingNavigation(null);
    navigate(path);
  }, [navigate]);

  const handleContinueQuiz = useCallback(() => {
    setShowQuitModal(false);
    setPendingNavigation(null);
  }, []);

  const handleSaveAndLeave = useCallback(() => {
    // Abandon (avec sauvegarde) : combien de questions répondues avant de quitter.
    trackQuizAbandon('practice', examId ?? '', session.results.length, session.totalQuestions);
    session.saveForLater();
    toast.success('Session enregistrée', {
      description: 'Vous pourrez reprendre là où vous vous êtes arrêté.',
    });
    const dest = pendingNavigation ?? `/exam/${examId}`;
    navigateAway(dest);
  }, [session, pendingNavigation, examId, navigateAway]);

  const handleLeaveWithoutSaving = useCallback(() => {
    trackQuizAbandon('practice', examId ?? '', session.results.length, session.totalQuestions);
    session.clearSavedSession();
    const dest = pendingNavigation ?? `/exam/${examId}`;
    navigateAway(dest);
  }, [session, pendingNavigation, examId, navigateAway]);

  const handleBackClick = () => {
    if (session.isSessionInProgress) {
      openQuitModal(`/exam/${examId}`);
    } else {
      navigate(`/exam/${examId}`);
    }
  };

  const handleAskAI = () => {
    toast.info('Fonctionnalité bientôt disponible', {
      description: "L'assistant IA pour expliquer les réponses n'est pas encore activé.",
    });
  };

  if (examLoading || session.sessionState === 'loading') {
    return (
      <div className="flex items-center justify-center h-full bg-app">
        <div className="w-8 h-8 border-2 border-green-500/30 border-t-green-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!exam || !examId || !categoryId) {
    return (
      <div className="flex items-center justify-center h-full bg-app">
        <div className="text-center px-4">
          <p className="text-lg font-medium text-fg-muted">Examen introuvable</p>
          <button onClick={() => navigate('/dashboard')} className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            Retour
          </button>
        </div>
      </div>
    );
  }

  if (session.sessionState === 'pending-resume' && session.pendingSavedSession) {
    return (
      <>
        <div className="flex items-center justify-center h-full bg-app">
          <div className="w-8 h-8 border-2 border-green-500/30 border-t-green-500 rounded-full animate-spin" />
        </div>
        <ResumePracticeModal
          answeredCount={session.pendingSavedSession.results.length}
          totalQuestions={session.pendingSavedSession.questions.length}
          onResume={session.resumeSavedSession}
          onRestart={session.discardSavedSessionAndRestart}
        />
      </>
    );
  }

  if (session.totalQuestions === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-app px-4">
        <BookOpen className="w-16 h-16 text-fg-faint mb-4" />
        <p className="text-lg font-medium text-fg-muted mb-2">Aucune question disponible</p>
        <p className="text-sm text-fg-faint mb-6 text-center">Ajoutez des questions à cette catégorie depuis l'admin.</p>
        <button onClick={() => navigate(`/exam/${examId}`)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm">
          Retour à l'examen
        </button>
      </div>
    );
  }

  if (session.sessionState === 'completed') {
    return (
      <div className="flex flex-col h-full bg-app pb-20 lg:pb-0">
        <div className="px-4 py-3 border-b border-line flex-shrink-0">
          <button onClick={() => navigate(`/exam/${examId}`)} className="flex items-center gap-2 text-fg-subtle hover:text-fg transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Retour</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <ResultsSummary
            results={session.results}
            questions={session.questions}
            examId={examId}
            categoryId={categoryId}
            examName={exam.name}
            totalTime={session.totalTimeElapsed}
            totalPoints={session.totalPoints}
            passingScore={exam.passingScore}
            onRestart={session.restartSession}
          />
        </div>
      </div>
    );
  }

  const isAnswered = session.answerState !== 'unanswered';
  const isLast = session.currentIndex >= session.totalQuestions - 1;

  return (
    <>
      <div className="flex h-full bg-app">
        <div className="flex-1 flex flex-col pb-20 lg:pb-0 overflow-hidden">
          <div className="px-4 sm:px-6 py-3 border-b border-line flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <button onClick={handleBackClick} className="flex items-center gap-2 text-fg-subtle hover:text-fg transition-colors">
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium hidden sm:inline">Retour</span>
              </button>
              <h1 className="text-sm font-semibold text-fg-muted truncate mx-4">{exam.name}</h1>
              <div className="flex items-center gap-2">
                <span className="text-xs text-fg-faint">
                  {session.correctCount}/{session.results.length}
                </span>
              </div>
            </div>
            <ProgressBar
              current={session.currentIndex}
              total={session.totalQuestions}
              results={session.results}
            />
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
            {session.currentQuestion && (
              <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
                <QuestionCard
                  question={session.currentQuestion}
                  questionNumber={session.currentIndex + 1}
                  totalQuestions={session.totalQuestions}
                />

                <OptionsList
                  options={session.currentQuestion.options}
                  selectedIndex={session.selectedAnswer}
                  correctIndex={isAnswered ? session.currentQuestion.correctAnswer : undefined}
                  answerState={session.answerState}
                  onSelect={session.selectAnswer}
                  disabled={isAnswered}
                />

                {isAnswered && (
                  <div ref={feedbackRef}>
                    <FeedbackPanel
                      key={session.currentQuestion.id}
                      isCorrect={session.answerState === 'correct'}
                      correctIndex={session.currentQuestion.correctAnswer}
                      explanation={session.currentQuestion.explanation}
                      onAskAI={handleAskAI}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {session.currentQuestion && (!isAnswered ? session.selectedAnswer !== null : true) && (
            <div className="px-4 sm:px-6 py-3 border-t border-line flex-shrink-0">
              <div className="max-w-2xl mx-auto">
                {!isAnswered ? (
                  <button
                    onClick={session.submitAnswer}
                    className="w-full py-3.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl font-semibold text-sm hover:from-green-700 hover:to-green-800 transition-all active:scale-[0.98]"
                  >
                    Valider ma réponse
                  </button>
                ) : (
                  <div className="flex gap-2.5">
                    {session.answerState === 'incorrect' && session.currentQuestion?.explanation && (
                      <button
                        onClick={() => feedbackRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                        className="flex items-center justify-center gap-1.5 px-4 py-3.5 rounded-xl text-sm font-medium bg-violet-500/10 border border-violet-500/20 text-violet-400 hover:bg-violet-500/15 transition-all active:scale-[0.98]"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden sm:inline">{locale === 'en' ? 'Explanation' : 'Explication'}</span>
                      </button>
                    )}
                    <button
                      onClick={session.nextQuestion}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-3.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] ${
                        session.answerState === 'correct'
                          ? 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800'
                          : 'bg-overlay-2 border border-line text-fg-muted hover:bg-overlay-3'
                      }`}
                    >
                      {isLast ? (locale === 'en' ? 'See results' : 'Voir les résultats') : (locale === 'en' ? 'Next question' : 'Question suivante')}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showQuitModal && (
        <QuitPracticeModal
          answeredCount={session.results.length}
          totalQuestions={session.totalQuestions}
          onContinue={handleContinueQuiz}
          onSaveAndLeave={handleSaveAndLeave}
          onLeaveWithoutSaving={handleLeaveWithoutSaving}
        />
      )}
    </>
  );
}
